import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productsApi } from '@/lib/api-client';

export function useDepartments(params: Record<string, string | number | boolean | undefined> = {}) {
  return useQuery({
    queryKey: ['departments', params],
    queryFn: () => productsApi.get<{ data: any[] }>('/departments', params),
  });
}

export function useCreateDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => productsApi.post('/departments', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
    },
  });
}

export function useUpdateDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => productsApi.put(`/departments/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
    },
  });
}

export function useDeleteDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, cascade }: { id: string; cascade?: boolean }) =>
      productsApi.delete(`/departments/${id}${cascade ? '?cascade=true' : ''}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

/// How many products belong to a department. Used to preview "Apply to N products?"
/// before firing the destructive cascade.
export function useDepartmentProductCount(id: string | null) {
  return useQuery({
    queryKey: ['department-product-count', id],
    queryFn: () => productsApi.get<{ data: { count: number } }>(`/departments/${id}/product-count`),
    enabled: !!id,
  });
}

/// Force-apply the department's current default toggles to every product
/// in the department. Use to reconcile drifted products (individual edits,
/// moved between depts, created before the default was set). Taxable, Coupon,
/// CRV and Tax Rate are per store: with a locationId only that store's values
/// change.
export function useApplyDepartmentDefaults() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, locationId }: { id: string; locationId?: string | null }) =>
      productsApi.post<{ data: { updated: number; departmentId: string } }>(`/departments/${id}/apply-defaults`, {
        locationId: locationId || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}
